import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Grid, 
  Paper,
  Button,
  Card,
  CardContent,
  Divider,
  Chip,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { 
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  LocationOn as LocationIcon,
  Work as WorkIcon,
  Timer as TimerIcon
} from '@mui/icons-material';
import LandingHeader from '../components/LandingHeader';
import Footer from '../components/Footer';

const CareersPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [selectedLocation, setSelectedLocation] = useState('all');

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
  };

  const handleDepartmentChange = (event: SelectChangeEvent) => {
    setSelectedDepartment(event.target.value);
  };

  const handleLocationChange = (event: SelectChangeEvent) => {
    setSelectedLocation(event.target.value);
  };

  const departments = [
    'Engineering',
    'Product',
    'Sales',
    'Marketing',
    'Customer Success',
    'Legal',
    'Operations',
    'Human Resources'
  ];

  const locations = [
    'San Francisco, CA',
    'New York, NY',
    'London, UK',
    'Remote'
  ];

  const jobOpenings = [
    {
      title: 'Senior Frontend Engineer',
      department: 'Engineering',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'We are looking for a Senior Frontend Engineer to join our team and help build the next generation of our AI-powered contract management platform. You will work closely with our product and design teams to create intuitive, responsive, and accessible user interfaces.',
      responsibilities: [
        'Design and implement new features for our web application using React, TypeScript, and Material-UI',
        'Collaborate with backend engineers to integrate frontend components with APIs',
        'Write clean, maintainable, and well-tested code',
        'Participate in code reviews and provide constructive feedback to other team members',
        'Optimize application performance and ensure cross-browser compatibility'
      ],
      requirements: [
        '5+ years of experience in frontend development',
        'Strong proficiency in React, TypeScript, and modern JavaScript',
        'Experience with responsive design and CSS frameworks',
        'Understanding of web accessibility standards',
        'Familiarity with version control systems (Git)',
        'Bachelor\'s degree in Computer Science or equivalent experience'
      ]
    },
    {
      title: 'Machine Learning Engineer',
      department: 'Engineering',
      location: 'San Francisco, CA',
      type: 'Full-time',
      description: 'We are seeking a talented Machine Learning Engineer to join our AI team. You will develop and implement machine learning models for natural language processing tasks related to legal document analysis and generation.',
      responsibilities: [
        'Design and implement machine learning models for text classification, entity extraction, and text generation',
        'Train and fine-tune large language models for legal domain-specific tasks',
        'Collaborate with product and engineering teams to integrate ML models into our platform',
        'Evaluate model performance and continuously improve accuracy and efficiency',
        'Stay current with the latest research and advancements in NLP and machine learning'
      ],
      requirements: [
        'MS or PhD in Computer Science, Machine Learning, or related field',
        '3+ years of experience in machine learning and natural language processing',
        'Proficiency in Python and ML frameworks such as TensorFlow or PyTorch',
        'Experience with transformer-based models (BERT, GPT, etc.)',
        'Knowledge of software engineering best practices',
        'Experience with cloud computing platforms (AWS, GCP, or Azure)'
      ]
    },
    {
      title: 'Product Manager',
      department: 'Product',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'We are looking for a Product Manager to help define and execute our product strategy. You will work closely with engineering, design, sales, and customer success teams to deliver features that solve real problems for legal professionals.',
      responsibilities: [
        'Define product requirements and create detailed specifications',
        'Prioritize features and build consensus around product roadmap',
        'Conduct user research and gather feedback to inform product decisions',
        'Work with engineering and design teams to deliver high-quality features',
        'Analyze product metrics and make data-driven decisions',
        'Communicate product updates to internal teams and customers'
      ],
      requirements: [
        '3+ years of experience in product management, preferably in B2B SaaS',
        'Strong analytical and problem-solving skills',
        'Excellent communication and interpersonal skills',
        'Experience with agile development methodologies',
        'Familiarity with legal tech or enterprise software is a plus',
        'Bachelor\'s degree in a relevant field'
      ]
    },
    {
      title: 'Account Executive',
      department: 'Sales',
      location: 'New York, NY',
      type: 'Full-time',
      description: 'We are seeking an experienced Account Executive to drive new business and expand our customer base. You will be responsible for the full sales cycle, from prospecting to closing deals with law firms and corporate legal departments.',
      responsibilities: [
        'Develop and execute strategic sales plans to meet or exceed quota',
        'Build relationships with key decision-makers at target organizations',
        'Conduct product demonstrations and communicate value proposition effectively',
        'Negotiate contracts and close deals',
        'Collaborate with customer success team for smooth customer onboarding',
        'Maintain accurate records in our CRM system'
      ],
      requirements: [
        '5+ years of B2B sales experience, preferably in SaaS or legal tech',
        'Track record of consistently meeting or exceeding sales targets',
        'Excellent presentation and negotiation skills',
        'Experience selling to legal departments or law firms is a plus',
        'Proficiency with CRM systems (Salesforce preferred)',
        'Bachelor\'s degree in a relevant field'
      ]
    },
    {
      title: 'Customer Success Manager',
      department: 'Customer Success',
      location: 'London, UK',
      type: 'Full-time',
      description: 'We are looking for a Customer Success Manager to ensure our clients achieve their goals using our platform. You will be the primary point of contact for a portfolio of customers, driving adoption, retention, and expansion.',
      responsibilities: [
        'Develop and maintain strong relationships with key stakeholders at client organizations',
        'Create and execute customer success plans to drive platform adoption',
        'Conduct regular check-ins and business reviews with clients',
        'Identify upsell and cross-sell opportunities',
        'Collaborate with product and engineering teams to advocate for customer needs',
        'Monitor customer health metrics and address issues proactively'
      ],
      requirements: [
        '3+ years of experience in customer success or account management',
        'Strong communication and relationship-building skills',
        'Experience with SaaS platforms and customer success methodologies',
        'Problem-solving mindset and ability to navigate complex organizations',
        'Familiarity with legal industry is a plus',
        'Bachelor\'s degree in a relevant field'
      ]
    },
    {
      title: 'Content Marketing Manager',
      department: 'Marketing',
      location: 'Remote',
      type: 'Full-time',
      description: 'We are seeking a Content Marketing Manager to develop and execute our content strategy. You will create compelling content that educates our target audience, generates leads, and establishes our company as a thought leader in legal tech.',
      responsibilities: [
        'Develop and manage content calendar across multiple channels',
        'Create high-quality blog posts, whitepapers, case studies, and other content',
        'Collaborate with subject matter experts to produce technical and thought leadership content',
        'Optimize content for SEO and lead generation',
        'Analyze content performance and refine strategy based on data',
        'Manage relationships with freelance writers and content creators'
      ],
      requirements: [
        '3+ years of experience in content marketing, preferably in B2B or SaaS',
        'Excellent writing and editing skills',
        'Understanding of SEO principles and content distribution strategies',
        'Experience with content management systems and marketing automation tools',
        'Ability to translate complex technical concepts into clear, compelling content',
        'Bachelor\'s degree in Marketing, Communications, or related field'
      ]
    }
  ];

  const filteredJobs = jobOpenings.filter(job => {
    const matchesSearch = searchQuery === '' || 
      job.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      job.description.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesDepartment = selectedDepartment === 'all' || 
      job.department === selectedDepartment;
    
    const matchesLocation = selectedLocation === 'all' || 
      job.location === selectedLocation;
    
    return matchesSearch && matchesDepartment && matchesLocation;
  });

  const benefits = [
    {
      title: 'Comprehensive Health Coverage',
      description: 'Medical, dental, and vision insurance for you and your dependents'
    },
    {
      title: 'Flexible PTO',
      description: 'Take time off when you need it, with our flexible paid time off policy'
    },
    {
      title: 'Remote Work Options',
      description: 'Work from home or our offices - we support flexible work arrangements'
    },
    {
      title: '401(k) Matching',
      description: 'We match 4% of your contributions to help you save for the future'
    },
    {
      title: 'Professional Development',
      description: 'Annual budget for conferences, courses, and learning resources'
    },
    {
      title: 'Parental Leave',
      description: '12 weeks of paid parental leave for all new parents'
    },
    {
      title: 'Wellness Program',
      description: 'Monthly wellness stipend for gym memberships or fitness classes'
    },
    {
      title: 'Company Equity',
      description: 'Stock options for all full-time employees'
    }
  ];

  return (
    <Box>
      {/* Hero Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 8, pt: 4 }}>
        <LandingHeader />
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" fontWeight="bold" gutterBottom>
            Careers
          </Typography>
          <Typography variant="h6" sx={{ maxWidth: '700px', mb: 4, opacity: 0.9 }}>
            Join our team and help shape the future of legal technology
          </Typography>
        </Container>
      </Box>
      
      {/* Our Culture */}
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Box 
              component="img"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2940&q=80"
              alt="Our Culture"
              sx={{ 
                width: '100%',
                borderRadius: 3,
                boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Our Culture
            </Typography>
            <Typography variant="body1" paragraph>
              At Contract AI, we're building a team of passionate individuals who are excited about using technology to transform the legal industry. We value innovation, collaboration, and impact in everything we do.
            </Typography>
            <Typography variant="body1" paragraph>
              We believe in creating an inclusive environment where diverse perspectives are valued and everyone has the opportunity to contribute meaningfully to our mission. Our team members come from a variety of backgrounds, including law, technology, design, and business.
            </Typography>
            <Typography variant="body1">
              We're committed to work-life balance and provide flexible work arrangements that allow our team members to do their best work while maintaining personal well-being. If you're excited about solving complex problems at the intersection of law and technology, we'd love to hear from you.
            </Typography>
          </Grid>
        </Grid>
      </Container>
      
      {/* Benefits */}
      <Box sx={{ bgcolor: '#f8faff', py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom textAlign="center">
            Benefits & Perks
          </Typography>
          <Typography variant="body1" paragraph textAlign="center" sx={{ maxWidth: '700px', mx: 'auto', mb: 6 }}>
            We offer competitive compensation and benefits to support our team members' well-being
          </Typography>
          
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper 
                  sx={{ 
                    p: 3, 
                    height: '100%',
                    borderRadius: 2,
                    transition: '0.3s',
                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <Typography variant="h6" fontWeight="bold" gutterBottom>
                    {benefit.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {benefit.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Job Openings */}
      <Box id="openings" sx={{ py: 8 }}>
        <Container maxWidth="lg">
          <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
            Open Positions
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: '700px', mb: 4 }}>
            Explore our current job openings and find your next opportunity
          </Typography>
          
          {/* Search and Filter */}
          <Grid container spacing={3} sx={{ mb: 4 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search positions..."
                variant="outlined"
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="department-label">Department</InputLabel>
                <Select
                  labelId="department-label"
                  value={selectedDepartment}
                  onChange={handleDepartmentChange}
                  label="Department"
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map((dept, index) => (
                    <MenuItem key={index} value={dept}>{dept}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth variant="outlined">
                <InputLabel id="location-label">Location</InputLabel>
                <Select
                  labelId="location-label"
                  value={selectedLocation}
                  onChange={handleLocationChange}
                  label="Location"
                >
                  <MenuItem value="all">All Locations</MenuItem>
                  {locations.map((loc, index) => (
                    <MenuItem key={index} value={loc}>{loc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          {/* Job Listings */}
          {filteredJobs.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
              <Typography variant="h6" color="text.secondary">
                No positions found matching your criteria
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Try adjusting your filters or search terms
              </Typography>
              <Button 
                variant="contained" 
                color="primary"
                onClick={() => {
                  setSearchQuery('');
                  setSelectedDepartment('all');
                  setSelectedLocation('all');
                }}
                sx={{ mt: 3 }}
              >
                Clear Filters
              </Button>
            </Paper>
          ) : (
            <Box>
              {filteredJobs.map((job, index) => (
                <Accordion 
                  key={index} 
                  sx={{ 
                    mb: 2, 
                    borderRadius: 2, 
                    overflow: 'hidden',
                    '&:before': {
                      display: 'none',
                    },
                    boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
                  }}
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ 
                      p: 3,
                      '&:hover': {
                        bgcolor: 'rgba(0,0,0,0.02)',
                      }
                    }}
                  >
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} sm={6}>
                        <Typography variant="h6" fontWeight="bold">
                          {job.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                          {job.department}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                          <Chip 
                            icon={<LocationIcon fontSize="small" />} 
                            label={job.location} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} 
                          />
                          <Chip 
                            icon={<TimerIcon fontSize="small" />} 
                            label={job.type} 
                            size="small" 
                            sx={{ bgcolor: 'rgba(0,0,0,0.05)' }} 
                          />
                        </Box>
                      </Grid>
                    </Grid>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 3 }}>
                    <Typography variant="body1" paragraph>
                      {job.description}
                    </Typography>
                    
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom sx={{ mt: 3 }}>
                      Responsibilities:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                      {job.responsibilities.map((item, i) => (
                        <Box component="li" key={i} sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    
                    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                      Requirements:
                    </Typography>
                    <Box component="ul" sx={{ pl: 2, mb: 3 }}>
                      {job.requirements.map((item, i) => (
                        <Box component="li" key={i} sx={{ mb: 1 }}>
                          <Typography variant="body2">
                            {item}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                    
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                      <Button 
                        variant="contained" 
                        color="primary"
                        sx={{ borderRadius: 2, py: 1, px: 3 }}
                      >
                        Apply Now
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Container>
      </Box>
      
      {/* CTA Section */}
      <Box sx={{ bgcolor: '#1a365d', color: 'white', py: 6 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" component="h2" fontWeight="bold" gutterBottom>
              Don't See a Perfect Match?
            </Typography>
            <Typography variant="body1" paragraph sx={{ maxWidth: '700px', mx: 'auto', mb: 4, opacity: 0.9 }}>
              We're always interested in connecting with talented individuals. Send us your resume and we'll keep you in mind for future opportunities.
            </Typography>
            <Button 
              variant="contained" 
              color="primary"
              size="large"
              href="mailto:careers@contractai.com"
              sx={{ 
                bgcolor: 'white', 
                color: '#1a365d',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.9)' },
                py: 1.5,
                px: 4,
                borderRadius: 2
              }}
            >
              Contact Our Recruiting Team
            </Button>
          </Box>
        </Container>
      </Box>
      
      <Footer />
    </Box>
  );
};

export default CareersPage; 